// `ColorToken` — a design token naming a colour, never a raw hex (F-ORG-03 rule 5, spec/BRAND.md §4).
//
// A station class and a zone each carry one: it colours the class's floor tiles and filter chips and
// the zone's rectangle on the plan (spec/13 §3.1, §3.4). A token rather than a hex because a brand
// replaces the palette at runtime (spec/03 §12): a tile coloured `#E8B23E` would stay that colour
// under every brand; one coloured `warn-ink` follows the theme.
//
// **Which tokens.** Not every token is a colour a tile can wear: `bg`, `surface` and `line` are the
// grounds the tile sits on, the tints and shadows are not colours at all, the aliases (`session`,
// `chart-2`) repeat a colour already listed. Every name an earlier palette's list carried (Voltage,
// Cobalt, Obsidian) is still here, so a stored class colour keeps resolving; Eclipse (ADR-0060) added
// `flare`, its highlight, and `on-reward` (white on haze), which clear the same bar. So the list is the colour tokens of
// `packages/config/tokens` that clear WCAG 1.4.11's 3:1 against **every** ground of the dark theme —
// the rule a coloured marker has to meet to be seen. `src/__tests__/color-token.test.ts` recomputes
// it from the token source and fails the build if this list and the tokens drift apart. The console's
// light theme (ADR-0041) does not narrow it — the list is an API value set — and the categorical
// palette, which is all the picker offers, is held to 3:1 in the light theme too.
//
// **The categorical palette.** The token set above was designed for interface chrome, not for telling
// eight classes apart, and it showed: the list was short and three of its entries carried a meaning
// (ok, warn, danger). #411 answered the open question — the product owner added a **categorical
// palette**, `category-1 … category-8` (ADR-0025 superseding ADR-0015, BRAND.md §4.6): eight distinct,
// equally-weighted hues that carry no meaning. They join `clavian.tokens.json` and, clearing the same
// 3:1 rule, flow into this list on their own. The console's class/zone picker now offers **only** the
// category tokens; the semantic and grey tokens stay valid here so a class already stored on one keeps
// resolving, but they are no longer offered for a new choice (see apps/console `stations/fields.tsx`).
import { z } from "../zod.js";
export const COLOR_TOKENS = [
    'ink',
    'muted',
    'subtle',
    'line-strong',
    'ok-ink',
    'warn-ink',
    'danger-ink',
    'category-1',
    'category-2',
    'category-3',
    'category-4',
    'category-5',
    'category-6',
    'category-7',
    'category-8',
    'accent',
    'accent-hi',
    'on-accent',
    'accent-ink',
    'focus',
    'surge',
    'reward',
    'reward-ink',
    'on-reward',
    'flare',
    'ok',
    'warn',
    'danger',
    'danger-hi',
    'chart-5',
];
export const CATEGORY_COLOR_TOKENS = COLOR_TOKENS.filter((token) => token.startsWith('category-'));
export const ColorTokenSchema = z.enum(COLOR_TOKENS).openapi('ColorToken', {
    description: 'A colour design token (spec/BRAND.md §4), never a hex: the colour tokens that stay visible on every ground in ' +
        'every theme.',
    example: 'accent-ink',
});
//# sourceMappingURL=color-token.js.map